import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Customers.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const fmtDate = (s) => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SALUTATIONS = ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'];

const emptyCustomer = {
  customer_name: '', salutation: '', primary_email: '', secondary_email: '',
  secondary_phone: '', dob: '', customer_type: '', status: 'A', concierge_id: '',
};

const emptyAddress = { address: '', pincode: '', label: 'Home', is_default: false, use_shared: false, shared_address_id: '' };

const emptyUser = { phone: '', email: '', password: '', user_type: 'C' };

const emptyInt = {
  interaction_type: '', direction: '', channel: '',
  summary: '', outcome: '', next_action: '', next_action_date: '',
};

const Customers = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${user?.token}` } }), [user]);
  const hasPerm = (n) => user?.permissions?.some((p) => p.name === n) ?? false;

  const canRead      = hasPerm('customers.read');
  const canCreate    = hasPerm('customers.create');
  const canUpdate    = hasPerm('customers.update');
  const canInteract  = hasPerm('customers.interaction');

  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [success, setSuccess]       = useState('');
  const [modalError, setModalError] = useState('');
  const [search, setSearch]         = useState(location.state?.search ?? '');
  const [currentPage, setCurrentPage] = useState(location.state?.page ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);

  // page-level tab
  const [pageTab, setPageTab] = useState('customers');

  // shared mine/all filter
  const [custFilter, setCustFilter] = useState('all');

  // renewals tab state
  const [renewals, setRenewals]         = useState([]);
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [renewalSearch, setRenewalSearch]   = useState('');
  const [renewalPage, setRenewalPage]       = useState(1);
  const [renewalTotal, setRenewalTotal]     = useState(0);
  const [renewalPages, setRenewalPages]     = useState(1);
  const [renewalType, setRenewalType]       = useState(['KIT', 'PUB', 'PMB']);
  const [renewalWhen, setRenewalWhen]       = useState('overdue');

  // modal state
  const [showModal, setShowModal]         = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [activeTab, setActiveTab]         = useState('details');
  const [saving, setSaving]               = useState(false);
  const [newPassword, setNewPassword]     = useState('');
  const [editUserType, setEditUserType]   = useState('C');
  const [formData, setFormData]           = useState(emptyCustomer);
  const [addressForm, setAddressForm]     = useState(emptyAddress);
  const [showAddrForm, setShowAddrForm]   = useState(false);
  const [savingAddr, setSavingAddr]       = useState(false);
  const [deletingAddr, setDeletingAddr]   = useState(null);
  const [createUser, setCreateUser]       = useState(false);
  const [userForm, setUserForm]           = useState(emptyUser);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [sharedAddresses, setSharedAddresses]     = useState([]);
  const [concierges, setConcierges]               = useState([]);
  const [editingAddr, setEditingAddr]             = useState(null); // link_id being edited
  const [editAddrForm, setEditAddrForm]           = useState({ address: '', pincode: '', label: '', is_default: false });
  const [savingEditAddr, setSavingEditAddr]       = useState(false);

  // wallet modal
  const [walletCustomer, setWalletCustomer]   = useState(null);
  const [walletBalance, setWalletBalance]     = useState(null);
  const [walletTxns, setWalletTxns]           = useState([]);
  const [walletTotal, setWalletTotal]         = useState(0);
  const [walletPages, setWalletPages]         = useState(1);
  const [walletPage, setWalletPage]           = useState(1);
  const [walletLoading, setWalletLoading]     = useState(false);
  const [walletError, setWalletError]         = useState('');

  // load-from-receipt sub-modal
  const [showLoadModal, setShowLoadModal]     = useState(false);
  const [receipts, setReceipts]               = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptSearch, setReceiptSearch]     = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [loadAmount, setLoadAmount]           = useState('');
  const [loadSaving, setLoadSaving]           = useState(false);
  const [loadError, setLoadError]             = useState('');

  // interactions modal
  const [showIntModal, setShowIntModal]     = useState(false);
  const [intCustomer, setIntCustomer]       = useState(null);
  const [interactions, setInteractions]     = useState([]);
  const [loadingInts, setLoadingInts]       = useState(false);
  const [intForm, setIntForm]               = useState(emptyInt);
  const [savingInt, setSavingInt]           = useState(false);
  const [deletingInt, setDeletingInt]       = useState(null);
  const [intError, setIntError]             = useState('');
  const [intFormOpen, setIntFormOpen]       = useState(false);

  const LIMIT = 20;

  const fetchCustomers = useCallback(async () => {
    if (!canRead) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/customers`, {
        params: { page: currentPage, limit: LIMIT, search, mine: custFilter === 'mine' ? 'true' : undefined },
        ...auth(),
      });
      setCustomers(res.data.customers || []);
      setTotalPages(res.data.pagination?.totalPages || 1);
      setTotal(res.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [canRead, currentPage, search, custFilter, auth]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchRenewals = useCallback(async () => {
    if (!canRead) return;
    if (renewalType.length === 0) {
      setRenewals([]);
      setRenewalTotal(0);
      setRenewalPages(1);
      return;
    }
    try {
      setRenewalLoading(true);
      const res = await axios.get(`${API_URL}/customers/renewals`, {
        params: { page: renewalPage, limit: 20, search: renewalSearch, type: renewalType.join(','), when: renewalWhen, mine: custFilter === 'mine' ? 'true' : undefined },
        ...auth(),
      });
      setRenewals(res.data.renewals || []);
      setRenewalTotal(res.data.pagination?.total || 0);
      setRenewalPages(res.data.pagination?.totalPages || 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load renewals');
    } finally {
      setRenewalLoading(false);
    }
  }, [canRead, renewalPage, renewalSearch, renewalType, renewalWhen, custFilter, auth]);

  useEffect(() => {
    if (pageTab === 'renewals') fetchRenewals();
  }, [fetchRenewals, pageTab]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  // ── modal helpers ────────────────────────────────────────────

  const loadSharedAddresses = () => {
    axios.get(`${API_URL}/addresses/shared/search?q=`, auth())
      .then(r => setSharedAddresses(r.data || []))
      .catch(() => {});
    axios.get(`${API_URL}/customers/concierges`, auth())
      .then(r => setConcierges(r.data || []))
      .catch(() => {});
  };

  const loadCustomerAddresses = async (customerId) => {
    try {
      const res = await axios.get(`${API_URL}/customers/${customerId}`, auth());
      setCustomerAddresses(res.data.addresses || []);
    } catch { /* non-fatal */ }
  };

  const openCreate = () => {
    setEditingCustomer(null);
    setFormData(emptyCustomer);
    setAddressForm(emptyAddress);
    setShowAddrForm(false);
    setCreateUser(false);
    setUserForm(emptyUser);
    setActiveTab('details');
    setModalError('');
    setShowModal(true);
    loadSharedAddresses();
  };

  const openEdit = async (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_name:  customer.customer_name  || '',
      salutation:     customer.salutation     || '',
      primary_email:  customer.primary_email  || '',
      secondary_email: customer.secondary_email || '',
      secondary_phone: customer.secondary_phone || '',
      dob:            customer.dob ? customer.dob.split('T')[0] : '',
      customer_type:  customer.customer_type  || '',
      status:         customer.status         || 'A',
      concierge_id:   customer.concierge_id   || '',
    });
    setAddressForm(emptyAddress);
    setShowAddrForm(false);
    setActiveTab('details');
    setCustomerAddresses([]);
    setEditingAddr(null);
    setModalError('');
    setShowModal(true);
    loadSharedAddresses();
    // fetch full detail with addresses
    try {
      const res = await axios.get(`${API_URL}/customers/${customer.id}`, auth());
      setCustomerAddresses(res.data.addresses || []);
      setEditUserType(res.data.user_type || 'C');
    } catch {
      // non-fatal
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCustomer(null);
    setSaving(false);
    setNewPassword('');
    setEditUserType('C');
    setShowAddrForm(false);
    setModalError('');
  };

  // ── save customer ────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name.trim()) {
      setModalError('Customer name is required');
      return;
    }
    setSaving(true);
    setModalError('');
    try {
      if (editingCustomer) {
        await axios.put(`${API_URL}/customers/${editingCustomer.id}`,
          { ...formData, concierge_id: formData.concierge_id || null, edit_user_type: editUserType, ...(newPassword.trim() ? { new_password: newPassword.trim() } : {}) },
          auth());
        setSuccess('Customer updated');
      } else {
        const payload = {
          ...formData,
          address: (addressForm.use_shared ? !!addressForm.shared_address_id : !!addressForm.address.trim()) ? addressForm : undefined,
          createUser: createUser ? { ...userForm, enabled: true } : undefined,
        };
        await axios.post(`${API_URL}/customers`, payload, auth());
        setSuccess('Customer created');
      }
      closeModal();
      fetchCustomers();
    } catch (err) {
      setModalError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── address management (edit mode) ──────────────────────────

  const handleAddAddress = async () => {
    if (addressForm.use_shared ? !addressForm.shared_address_id : !addressForm.address.trim()) return;
    setSavingAddr(true);
    try {
      const payload = customerAddresses.length === 0
        ? { ...addressForm, is_default: true }
        : addressForm;
      await axios.post(
        `${API_URL}/customers/${editingCustomer.id}/addresses`,
        payload, auth()
      );
      await loadCustomerAddresses(editingCustomer.id);
      setAddressForm(emptyAddress);
      setShowAddrForm(false);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to add address');
    } finally {
      setSavingAddr(false);
    }
  };

  const handleDeleteAddress = async (linkId) => {
    setDeletingAddr(linkId);
    try {
      const res = await axios.delete(`${API_URL}/customers/${editingCustomer.id}/addresses/${linkId}`, auth());
      const { wasDefault, newDefault } = res.data;
      setCustomerAddresses(prev => {
        const remaining = prev.filter(a => a.link_id !== linkId);
        if (wasDefault && newDefault) {
          return remaining.map(a =>
            a.link_id === newDefault.link_id ? { ...a, is_default: true } : { ...a, is_default: false }
          );
        }
        return remaining;
      });
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to remove address');
    } finally {
      setDeletingAddr(null);
    }
  };

  const handleUpdateAddress = async (linkId) => {
    if (!editAddrForm.address.trim()) return;
    setSavingEditAddr(true);
    try {
      await axios.put(
        `${API_URL}/customers/${editingCustomer.id}/addresses/${linkId}`,
        editAddrForm, auth()
      );
      await loadCustomerAddresses(editingCustomer.id);
      setEditingAddr(null);
    } catch (err) {
      setModalError(err.response?.data?.error || 'Failed to update address');
    } finally {
      setSavingEditAddr(false);
    }
  };

  // ── interaction handlers ─────────────────────────────────────

  const openInteractions = async (customer) => {
    setIntCustomer(customer);
    setIntForm(emptyInt);
    setIntError('');
    setIntFormOpen(false);
    setShowIntModal(true);
    setLoadingInts(true);
    try {
      const res = await axios.get(`${API_URL}/customers/${customer.id}/interactions`, auth());
      setInteractions(res.data || []);
    } catch (err) {
      setIntError(err.response?.data?.error || 'Failed to load interactions');
    } finally {
      setLoadingInts(false);
    }
  };

  const handleSaveInteraction = async () => {
    if (!intForm.interaction_type.trim()) return;
    setSavingInt(true);
    setIntError('');
    try {
      const res = await axios.post(`${API_URL}/customers/${intCustomer.id}/interactions`, intForm, auth());
      setInteractions(prev => [res.data, ...prev]);
      setIntForm(emptyInt);
    } catch (err) {
      setIntError(err.response?.data?.error || 'Failed to save interaction');
    } finally {
      setSavingInt(false);
    }
  };

  const handleDeleteInteraction = async (intId) => {
    setDeletingInt(intId);
    try {
      await axios.delete(`${API_URL}/customers/${intCustomer.id}/interactions/${intId}`, auth());
      setInteractions(prev => prev.filter(i => i.id !== intId));
    } catch (err) {
      setIntError(err.response?.data?.error || 'Failed to delete interaction');
    } finally {
      setDeletingInt(null);
    }
  };

  // ── wallet ───────────────────────────────────────────────────

  const fetchWallet = async (customerId, page = 1) => {
    setWalletLoading(true);
    setWalletError('');
    try {
      const res = await axios.get(`${API_URL}/wallet/admin/${customerId}?page=${page}`, auth());
      setWalletBalance(res.data.balance);
      setWalletTxns(res.data.transactions || []);
      setWalletTotal(res.data.total || 0);
      setWalletPages(res.data.pages || 1);
      setWalletPage(page);
    } catch (err) {
      setWalletError(err.response?.data?.error || 'Failed to load wallet');
    } finally {
      setWalletLoading(false);
    }
  };

  const openWallet = (customer) => {
    setWalletCustomer(customer);
    setWalletBalance(null);
    setWalletTxns([]);
    setWalletError('');
    setWalletPage(1);
    fetchWallet(customer.id, 1);
  };

  const closeWallet = () => { setWalletCustomer(null); setShowLoadModal(false); };

  const openLoadModal = async () => {
    setShowLoadModal(true);
    setSelectedReceipt(null);
    setLoadAmount('');
    setLoadError('');
    setReceiptSearch('');
    setReceiptsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/admin/receipts`, auth());
      setReceipts(res.data || []);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Failed to load receipts');
    } finally {
      setReceiptsLoading(false);
    }
  };

  const searchReceipts = async (q) => {
    setReceiptSearch(q);
    setReceiptsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/wallet/admin/receipts?search=${encodeURIComponent(q)}`, auth());
      setReceipts(res.data || []);
    } catch { /* ignore */ } finally {
      setReceiptsLoading(false);
    }
  };

  const handleSelectReceipt = (r) => {
    setSelectedReceipt(r);
    setLoadAmount(parseFloat(r.balance_amount).toFixed(2));
    setLoadError('');
  };

  const handleLoadFromReceipt = async () => {
    if (!selectedReceipt || !loadAmount) return;
    const amt = parseFloat(loadAmount);
    if (isNaN(amt) || amt <= 0) { setLoadError('Enter a valid amount'); return; }
    if (amt > parseFloat(selectedReceipt.balance_amount) + 0.001) {
      setLoadError(`Amount cannot exceed available balance ₹${parseFloat(selectedReceipt.balance_amount).toFixed(2)}`);
      return;
    }
    setLoadSaving(true);
    setLoadError('');
    try {
      await axios.post(
        `${API_URL}/wallet/admin/${walletCustomer.id}/load-from-receipt`,
        { receipt_id: selectedReceipt.id, amount: amt },
        auth()
      );
      setShowLoadModal(false);
      fetchWallet(walletCustomer.id, 1);
      setSuccess(`Wallet loaded with ₹${amt.toFixed(2)}`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Load failed');
    } finally {
      setLoadSaving(false);
    }
  };

  // ── render helpers ───────────────────────────────────────────

  return (
    <div className="cu-page">
      {/* Header */}
      <div className="cu-header">
        <div>
          <h1>Customers</h1>
          <p>Manage customer profiles, addresses and login accounts</p>
        </div>
        {canCreate && (
          <button className="cu-add-btn" onClick={openCreate}>
            <span>+</span> Add Customer
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠</span>{error}
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>{success}
          <button className="alert-close" onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      {/* Page-level tabs */}
      <div className="cu-page-tabs">
        <button className={`cu-page-tab${pageTab === 'customers' ? ' active' : ''}`} onClick={() => setPageTab('customers')}>Customers</button>
        <button className={`cu-page-tab${pageTab === 'renewals' ? ' active' : ''}`} onClick={() => setPageTab('renewals')}>Renewals</button>
      </div>

      {/* Mine / All filter */}
      <div className="cu-mine-filter">
        {['all', 'mine'].map(f => (
          <button
            key={f}
            className={`ren-chip${custFilter === f ? ' active' : ''}`}
            onClick={() => { setCustFilter(f); setCurrentPage(1); setRenewalPage(1); }}
          >
            {f === 'all' ? 'All' : 'My Customers'}
          </button>
        ))}
      </div>

      {/* ── Customers tab ── */}
      {pageTab === 'customers' && <>

      {/* Search */}
      <div className="cu-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            type="text"
            placeholder="Search by name, email or phone…"
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        <div className="cu-count">{total} customer{total !== 1 ? 's' : ''}</div>
      </div>

      {/* Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Addresses</th>
              <th>Status</th>
              <th>Created</th>
              {(canUpdate || canInteract) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="no-data">Loading…</td></tr>
            ) : customers.length === 0 ? (
              <tr><td colSpan={7} className="no-data">No customers found</td></tr>
            ) : customers.map(c => (
              <tr key={c.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">{(c.customer_name || '?')[0].toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 600 }}>
                        {c.salutation ? `${c.salutation} ` : ''}{c.customer_name}
                      </div>
                      {c.user_id && (
                        <span className="cu-user-badge">@{c.username}</span>
                      )}
                      {c.concierge_name && (
                        <span className="cu-concierge-badge">👤 {c.concierge_name}</span>
                      )}
                    </div>
                  </div>
                </td>
                <td>{c.phone || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>{c.primary_email || <span style={{ color: '#bbb' }}>—</span>}</td>
                <td>
                  {c.address_count > 0
                    ? <span className="cu-addr-badge">📍 {c.address_count}</span>
                    : <span style={{ color: '#bbb' }}>—</span>}
                </td>
                <td>
                  <span className={`status-badge ${c.status === 'A' ? 'status-active' : 'status-inactive'}`}>
                    {c.status === 'A' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>{fmtDate(c.created_date)}</td>
                {(canUpdate || canInteract) && (
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-interact"
                        title="View Orders"
                        onClick={() => navigate(
                          `/sales-orders?customerId=${c.id}&customerName=${encodeURIComponent(c.customer_name)}`,
                          { state: { search, page: currentPage } }
                        )}
                      >📋</button>
                      <button className="btn-interact" title="Wallet" onClick={() => openWallet(c)}>👛</button>
                      {canInteract && (
                        <button className="btn-interact" title="Interactions" onClick={() => openInteractions(c)}>💬</button>
                      )}
                      {canUpdate && (
                        <button className="btn-edit" title="Edit" onClick={() => openEdit(c)}>✏️</button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => p - 1)}
            disabled={currentPage === 1 || loading}
          >
            ← Previous
          </button>
          <div className="pagination-numbers">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-number${currentPage === pageNumber ? ' active' : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                    disabled={loading}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                return <span key={pageNumber} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
          </div>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}

      </>}

      {/* ── Renewals tab ── */}
      {pageTab === 'renewals' && <>

      {/* Renewals search */}
      <div className="cu-controls">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            className="search-input"
            type="text"
            placeholder="Search by name or phone…"
            value={renewalSearch}
            onChange={e => { setRenewalSearch(e.target.value); setRenewalPage(1); }}
          />
        </div>
        <div className="cu-count">{renewalTotal} result{renewalTotal !== 1 ? 's' : ''}</div>
      </div>

      {/* Renewals filters */}
      <div className="ren-filters">
        <div className="ren-chip-group">
          {['overdue', 'upcoming'].map(w => (
            <button
              key={w}
              className={`ren-chip${renewalWhen === w ? ' active' : ''}`}
              onClick={() => { setRenewalWhen(w); setRenewalPage(1); }}
            >
              {w === 'overdue' ? 'Overdue' : 'Upcoming'}
            </button>
          ))}
        </div>
        <div className="ren-chip-group">
          {['KIT', 'PUB', 'PMB', 'Regular'].map(t => (
            <button
              key={t}
              className={`ren-chip${renewalType.includes(t) ? ' active' : ''}`}
              onClick={() => {
                setRenewalType(prev =>
                  prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]
                );
                setRenewalPage(1);
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Renewals table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Order #</th>
              <th>Type</th>
              <th>Delivery Date</th>
              <th>Days</th>
              {(canUpdate || canInteract) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {renewalLoading ? (
              <tr><td colSpan={8} className="no-data">Loading…</td></tr>
            ) : renewals.length === 0 ? (
              <tr><td colSpan={8} className="no-data">No renewals found</td></tr>
            ) : renewals.map(r => {
              const daysDiff = r.days_diff != null ? parseInt(r.days_diff, 10) : null;
              return (
                <tr key={`${r.order_id}-${r.order_type}`}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">{(r.customer_name || '?')[0].toUpperCase()}</div>
                      <div style={{ fontWeight: 600 }}>{r.customer_name}</div>
                    </div>
                  </td>
                  <td>{r.phone || <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>
                    <span className={`status-badge ${r.customer_status === 'A' ? 'status-active' : 'status-inactive'}`}>
                      {r.customer_status === 'A' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.order_no}</td>
                  <td><span className={`ren-type-badge ren-type-${r.order_type.toLowerCase()}`}>{r.order_type}</span></td>
                  <td>{r.delivery_date ? fmtDate(String(r.delivery_date).slice(0, 10) + 'T12:00:00') : <span style={{ color: '#bbb' }}>—</span>}</td>
                  <td>
                    {daysDiff == null ? <span style={{ color: '#bbb' }}>—</span>
                      : daysDiff > 0
                        ? <span className="ren-days ren-days--overdue">{daysDiff}d ago</span>
                        : daysDiff === 0
                          ? <span className="ren-days ren-days--today">Today</span>
                          : <span className="ren-days ren-days--upcoming">in {Math.abs(daysDiff)}d</span>
                    }
                  </td>
                  {(canUpdate || canInteract) && (
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-interact"
                          title="View Orders"
                          onClick={() => navigate(
                            `/sales-orders?customerId=${r.customer_id}&customerName=${encodeURIComponent(r.customer_name)}`,
                            { state: { fromRenewals: true } }
                          )}
                        >📋</button>
                        <button className="btn-interact" title="Wallet"
                          onClick={() => openWallet({ id: r.customer_id, customer_name: r.customer_name })}>👛</button>
                        {canInteract && (
                          <button className="btn-interact" title="Interactions"
                            onClick={() => openInteractions({ id: r.customer_id, customer_name: r.customer_name })}>💬</button>
                        )}
                        {canUpdate && (
                          <button className="btn-edit" title="Edit"
                            onClick={() => openEdit({ id: r.customer_id, customer_name: r.customer_name, salutation: '', primary_email: '', secondary_email: '', secondary_phone: '', dob: '', customer_type: '', status: r.customer_status, user_id: null })}>✏️</button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Renewals pagination */}
      {renewalPages > 1 && (
        <div className="pagination">
          <button className="pagination-btn" onClick={() => setRenewalPage(p => p - 1)} disabled={renewalPage === 1 || renewalLoading}>← Previous</button>
          <div className="pagination-numbers">
            {[...Array(renewalPages)].map((_, i) => {
              const pn = i + 1;
              if (pn === 1 || pn === renewalPages || (pn >= renewalPage - 1 && pn <= renewalPage + 1)) {
                return (
                  <button key={pn} className={`pagination-number${renewalPage === pn ? ' active' : ''}`}
                    onClick={() => setRenewalPage(pn)} disabled={renewalLoading}>{pn}</button>
                );
              } else if (pn === renewalPage - 2 || pn === renewalPage + 2) {
                return <span key={pn} className="pagination-ellipsis">...</span>;
              }
              return null;
            })}
          </div>
          <button className="pagination-btn" onClick={() => setRenewalPage(p => p + 1)} disabled={renewalPage === renewalPages || renewalLoading}>Next →</button>
        </div>
      )}

      </>}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="cu-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            {/* Tabs (only in edit mode) */}
            {editingCustomer && (
              <div className="cu-tabs">
                <button className={`cu-tab${activeTab === 'details' ? ' active' : ''}`} onClick={() => setActiveTab('details')}>Details</button>
                <button className={`cu-tab${activeTab === 'addresses' ? ' active' : ''}`} onClick={() => setActiveTab('addresses')}>
                  Addresses {customerAddresses.length > 0 ? `(${customerAddresses.length})` : ''}
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="cu-tab-body">
                {modalError && (
                  <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}>
                    <span className="alert-icon">⚠</span>{modalError}
                    <button className="alert-close" onClick={() => setModalError('')}>×</button>
                  </div>
                )}

                {/* ── Details tab ── */}
                {(!editingCustomer || activeTab === 'details') && (
                  <div className="cu-form-grid">

                    {/* ── Left column: customer fields ── */}
                    <div>
                      <div className="cu-section-title">Customer Details</div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Salutation</label>
                          <select
                            className="form-select"
                            value={formData.salutation}
                            onChange={e => setFormData(p => ({ ...p, salutation: e.target.value }))}
                          >
                            <option value="">—</option>
                            {SALUTATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Status</label>
                          <select
                            className="form-select"
                            value={formData.status}
                            onChange={e => setFormData(p => ({ ...p, status: e.target.value }))}
                          >
                            <option value="A">Active</option>
                            <option value="I">Inactive</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Customer Name <span style={{ color: 'red' }}>*</span></label>
                        <input
                          className="form-input"
                          type="text"
                          value={formData.customer_name}
                          onChange={e => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                          required
                          placeholder="Full name"
                        />
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Primary Email</label>
                          <input
                            className="form-input"
                            type="email"
                            value={formData.primary_email}
                            onChange={e => setFormData(p => ({ ...p, primary_email: e.target.value }))}
                            placeholder="email@example.com"
                          />
                        </div>
                        <div className="form-group">
                          <label>Secondary Email</label>
                          <input
                            className="form-input"
                            type="email"
                            value={formData.secondary_email}
                            onChange={e => setFormData(p => ({ ...p, secondary_email: e.target.value }))}
                            placeholder="email@example.com"
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Secondary Phone</label>
                          <input
                            className="form-input"
                            type="text"
                            value={formData.secondary_phone}
                            onChange={e => setFormData(p => ({ ...p, secondary_phone: e.target.value }))}
                            placeholder="Alternate phone"
                          />
                        </div>
                        <div className="form-group">
                          <label>Date of Birth</label>
                          <input
                            className="form-input"
                            type="date"
                            value={formData.dob}
                            onChange={e => setFormData(p => ({ ...p, dob: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Customer Type</label>
                          <select
                            className="form-select"
                            value={formData.customer_type}
                            onChange={e => setFormData(p => ({ ...p, customer_type: e.target.value }))}
                          >
                            <option value="">—</option>
                            <option value="I">Individual</option>
                            <option value="B">Business</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Concierge</label>
                          <select
                            className="form-select"
                            value={formData.concierge_id}
                            onChange={e => setFormData(p => ({ ...p, concierge_id: e.target.value }))}
                          >
                            <option value="">— None —</option>
                            {concierges.map(c => (
                              <option key={c.id} value={c.id}>{c.name}{c.phone ? ` (${c.phone})` : ''}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* ── Right column: address + user account ── */}
                    <div>
                      {/* Address section (create only) */}
                      {!editingCustomer && (
                        <>
                          <div className="cu-section-title">Address (optional)</div>
                          <label className="cu-user-toggle" style={{ marginBottom: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={addressForm.use_shared}
                              onChange={e => setAddressForm(p => ({ ...p, use_shared: e.target.checked, shared_address_id: '', address: '', pincode: '' }))}
                            />
                            <span>Use shared address</span>
                          </label>
                          {addressForm.use_shared ? (
                            <div className="form-group">
                              <label>Shared Address</label>
                              <select
                                className="form-select"
                                value={addressForm.shared_address_id}
                                onChange={e => {
                                  const sa = sharedAddresses.find(a => String(a.id) === e.target.value);
                                  setAddressForm(p => ({ ...p, shared_address_id: e.target.value, address: sa?.address || '', pincode: sa?.pincode || '' }));
                                }}
                              >
                                <option value="">— Select shared address —</option>
                                {sharedAddresses.map(sa => (
                                  <option key={sa.id} value={sa.id}>{sa.name} — {sa.address}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <>
                              <div className="form-group">
                                <label>Address</label>
                                <textarea
                                  className="form-input"
                                  rows={2}
                                  value={addressForm.address}
                                  onChange={e => setAddressForm(p => ({ ...p, address: e.target.value }))}
                                  placeholder="Street, area, city"
                                />
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Pincode</label>
                                  <input
                                    className="form-input"
                                    type="text"
                                    value={addressForm.pincode}
                                    onChange={e => setAddressForm(p => ({ ...p, pincode: e.target.value }))}
                                    placeholder="6-digit pincode"
                                    maxLength={10}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Label</label>
                                  <input
                                    className="form-input"
                                    type="text"
                                    value={addressForm.label}
                                    onChange={e => setAddressForm(p => ({ ...p, label: e.target.value }))}
                                    placeholder="Home / Office"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {/* Login Account (create) */}
                      {!editingCustomer && (
                        <>
                          <div className="cu-section-title">Login Account</div>
                          <label className="cu-user-toggle">
                            <input
                              type="checkbox"
                              checked={createUser}
                              onChange={e => setCreateUser(e.target.checked)}
                            />
                            <span>Create a login account for this customer</span>
                          </label>
                          {createUser && (
                            <>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Phone (username) <span style={{ color: 'red' }}>*</span></label>
                                  <input
                                    className="form-input"
                                    type="text"
                                    value={userForm.phone}
                                    onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))}
                                    placeholder="Mobile number"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Login Email</label>
                                  <input
                                    className="form-input"
                                    type="email"
                                    value={userForm.email}
                                    onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))}
                                    placeholder="Defaults to primary email"
                                  />
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>User Type</label>
                                  <input
                                    className="form-input"
                                    type="text"
                                    value={userForm.user_type}
                                    onChange={e => setUserForm(p => ({ ...p, user_type: e.target.value }))}
                                    placeholder="e.g. C"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Password <span style={{ color: 'red' }}>*</span></label>
                                  <input
                                    className="form-input"
                                    type="password"
                                    value={userForm.password}
                                    onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))}
                                    placeholder="Minimum 4 characters"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {/* Login Account (edit — only if user is linked) */}
                      {editingCustomer && editingCustomer.user_id && (
                        <>
                          <div className="cu-section-title">Login Account</div>
                          <div className="form-row">
                            <div className="form-group">
                              <label>User Type</label>
                              <input
                                className="form-input"
                                type="text"
                                value={editUserType}
                                onChange={e => setEditUserType(e.target.value)}
                                placeholder="e.g. C"
                              />
                            </div>
                            <div className="form-group">
                              <label>New Password <span style={{ color: '#999', fontWeight: 400 }}>(blank = no change)</span></label>
                              <input
                                className="form-input"
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="Minimum 4 characters"
                                minLength={4}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                  </div>
                )}

                {/* ── Addresses tab (edit mode) ── */}
                {editingCustomer && activeTab === 'addresses' && (
                  <>
                    <div className="cu-addr-list">
                      {customerAddresses.length === 0 && (
                        <p style={{ color: '#7f8c8d', fontSize: '0.875rem' }}>No addresses yet.</p>
                      )}
                      {customerAddresses.map(a => (
                        <div key={a.link_id} className="cu-addr-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          {editingAddr === a.link_id ? (
                            <div className="cu-edit-addr-form">
                              <div className="form-group">
                                <label>Address <span style={{ color: 'red' }}>*</span></label>
                                <textarea
                                  className="form-input"
                                  rows={2}
                                  value={editAddrForm.address}
                                  onChange={e => setEditAddrForm(p => ({ ...p, address: e.target.value }))}
                                />
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Pincode</label>
                                  <input className="form-input" type="text" maxLength={10}
                                    value={editAddrForm.pincode}
                                    onChange={e => setEditAddrForm(p => ({ ...p, pincode: e.target.value }))}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Label</label>
                                  <input className="form-input" type="text"
                                    value={editAddrForm.label}
                                    onChange={e => setEditAddrForm(p => ({ ...p, label: e.target.value }))}
                                  />
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  id={`edit-default-${a.link_id}`}
                                  checked={editAddrForm.is_default}
                                  disabled={a.is_default}
                                  onChange={e => setEditAddrForm(p => ({ ...p, is_default: e.target.checked }))}
                                />
                                <label htmlFor={`edit-default-${a.link_id}`} style={{ fontSize: '0.8rem' }}>
                                  Set as default address
                                </label>
                              </div>
                              <div className="cu-add-addr-actions">
                                <button type="button" className="cu-save-btn"
                                  disabled={savingEditAddr || !editAddrForm.address.trim()}
                                  onClick={() => handleUpdateAddress(a.link_id)}>
                                  {savingEditAddr ? 'Saving…' : 'Update'}
                                </button>
                                <button type="button" className="cu-cancel-btn" onClick={() => setEditingAddr(null)}>
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                              <div className="cu-addr-info">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                  <span className="cu-addr-label">{a.label}</span>
                                  {a.is_default && <span className="cu-addr-default">default</span>}
                                  {a.is_shared && <span className="cu-addr-default" style={{ background: '#e3f2fd', color: '#0d47a1' }}>shared</span>}
                                </div>
                                <div className="cu-addr-text">{a.address}</div>
                                {a.pincode && <div className="cu-addr-pin">📮 {a.pincode}</div>}
                              </div>
                              <div style={{ display: 'flex', gap: '0.15rem', flexShrink: 0 }}>
                                {!a.is_shared && (
                                  <button
                                    type="button"
                                    className="cu-addr-edit"
                                    title="Edit address"
                                    onClick={() => {
                                      setEditingAddr(a.link_id);
                                      setEditAddrForm({ address: a.address || '', pincode: a.pincode || '', label: a.label || '', is_default: a.is_default || false });
                                    }}
                                  >✏️</button>
                                )}
                                <button
                                  type="button"
                                  className="cu-addr-del"
                                  title={a.is_shared ? 'Unlink shared address' : 'Remove address'}
                                  disabled={deletingAddr === a.link_id}
                                  onClick={() => handleDeleteAddress(a.link_id)}
                                >
                                  {deletingAddr === a.link_id ? '…' : '🗑️'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {!showAddrForm && (
                      <button type="button" className="cu-add-addr-btn" onClick={() => setShowAddrForm(true)}>
                        + Add Address
                      </button>
                    )}

                    {showAddrForm && (
                      <div className="cu-add-addr-form">
                        <label className="cu-user-toggle" style={{ marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            checked={addressForm.use_shared}
                            onChange={e => setAddressForm(p => ({ ...p, use_shared: e.target.checked, shared_address_id: '', address: '', pincode: '' }))}
                          />
                          <span>Use shared address</span>
                        </label>
                        {addressForm.use_shared ? (
                          <div className="form-group">
                            <label>Shared Address <span style={{ color: 'red' }}>*</span></label>
                            <select
                              className="form-select"
                              value={addressForm.shared_address_id}
                              onChange={e => {
                                const sa = sharedAddresses.find(a => String(a.id) === e.target.value);
                                setAddressForm(p => ({ ...p, shared_address_id: e.target.value, address: sa?.address || '', pincode: sa?.pincode || '' }));
                              }}
                            >
                              <option value="">— Select shared address —</option>
                              {sharedAddresses
                                .filter(sa => !customerAddresses.some(ca => ca.address_id === sa.id))
                                .map(sa => (
                                  <option key={sa.id} value={sa.id}>{sa.name} — {sa.address}</option>
                                ))}
                            </select>
                          </div>
                        ) : (
                          <>
                            <div className="form-group">
                              <label>Address <span style={{ color: 'red' }}>*</span></label>
                              <textarea
                                className="form-input"
                                rows={2}
                                value={addressForm.address}
                                onChange={e => setAddressForm(p => ({ ...p, address: e.target.value }))}
                                placeholder="Street, area, city"
                              />
                            </div>
                            <div className="form-row">
                              <div className="form-group">
                                <label>Pincode</label>
                                <input
                                  className="form-input"
                                  type="text"
                                  value={addressForm.pincode}
                                  onChange={e => setAddressForm(p => ({ ...p, pincode: e.target.value }))}
                                  placeholder="6-digit pincode"
                                  maxLength={10}
                                />
                              </div>
                              <div className="form-group">
                                <label>Label</label>
                                <input
                                  className="form-input"
                                  type="text"
                                  value={addressForm.label}
                                  onChange={e => setAddressForm(p => ({ ...p, label: e.target.value }))}
                                  placeholder="Home / Office"
                                />
                              </div>
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            id="addr-default"
                            checked={customerAddresses.length === 0 ? true : addressForm.is_default}
                            disabled={customerAddresses.length === 0}
                            onChange={e => setAddressForm(p => ({ ...p, is_default: e.target.checked }))}
                          />
                          <label htmlFor="addr-default" style={{ fontSize: '0.8rem', cursor: customerAddresses.length === 0 ? 'default' : 'pointer' }}>
                            Set as default address{customerAddresses.length === 0 ? ' (only address)' : ''}
                          </label>
                        </div>
                        <div className="cu-add-addr-actions">
                          <button type="button" className="cu-save-btn"
                            disabled={savingAddr || (addressForm.use_shared ? !addressForm.shared_address_id : !addressForm.address.trim())}
                            onClick={handleAddAddress}>
                            {savingAddr ? 'Saving…' : 'Save Address'}
                          </button>
                          <button type="button" className="cu-cancel-btn" onClick={() => { setShowAddrForm(false); setAddressForm(emptyAddress); }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer — only show submit when on details tab */}
              {(!editingCustomer || activeTab === 'details') && (
                <div className="modal-footer">
                  <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                  <button type="submit" className="btn-submit" disabled={saving}>
                    {saving ? 'Saving…' : editingCustomer ? 'Save Changes' : 'Create Customer'}
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Wallet Modal ───────────────────────────────────── */}
      {walletCustomer && (
        <div className="modal-overlay" onClick={closeWallet}>
          <div className="int-modal" style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>👛 Wallet — {walletCustomer.customer_name}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {hasPerm('wallet.loadfromQR') && (
                  <button
                    className="btn-submit"
                    style={{ fontSize: '0.8rem', padding: '4px 12px' }}
                    onClick={openLoadModal}
                  >Load from QR</button>
                )}
                <button className="modal-close" onClick={closeWallet}>×</button>
              </div>
            </div>
            <div className="int-modal-body">
              {walletError && (
                <div className="alert alert-error" style={{ margin: '0.75rem 1.5rem 0' }}>
                  <span className="alert-icon">⚠</span>{walletError}
                </div>
              )}
              {walletBalance && (
                <div style={{ display: 'flex', gap: '1rem', padding: '1rem 1.5rem 0' }}>
                  <div style={{ flex: 1, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, marginBottom: '2px' }}>CASH BALANCE</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#15803d' }}>₹{walletBalance.cash_balance}</div>
                  </div>
                  <div style={{ flex: 1, background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#ca8a04', fontWeight: 600, marginBottom: '2px' }}>POINTS BALANCE</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#a16207' }}>{walletBalance.points_balance} pts</div>
                  </div>
                </div>
              )}
              <div className="int-list-section">
                <div className="cu-section-title">
                  Transactions {walletTotal > 0 && <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>({walletTotal})</span>}
                </div>
                {walletLoading ? (
                  <p style={{ color: '#7f8c8d', fontSize: '0.875rem' }}>Loading…</p>
                ) : walletTxns.length === 0 ? (
                  <p style={{ color: '#7f8c8d', fontSize: '0.875rem', fontStyle: 'italic' }}>No transactions yet.</p>
                ) : (
                  <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.825rem' }}>
                      <thead>
                        <tr style={{ background: '#f8f9fa' }}>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Date</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Type</th>
                          <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Description</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>Amount</th>
                          <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>Balance After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {walletTxns.map(t => {
                          const isCredit = t.txn_type !== 'SPEND';
                          return (
                            <tr key={t.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                              <td style={{ padding: '6px 8px', color: '#666', whiteSpace: 'nowrap' }}>
                                {new Date(t.created_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                              </td>
                              <td style={{ padding: '6px 8px' }}>
                                <span style={{
                                  fontSize: '0.7rem', fontWeight: 700, padding: '2px 6px', borderRadius: '10px',
                                  background: isCredit ? '#e8f8f0' : '#fdecea',
                                  color: isCredit ? '#27ae60' : '#e74c3c',
                                }}>
                                  {t.txn_type}
                                </span>
                              </td>
                              <td style={{ padding: '6px 8px', color: '#555' }}>{t.description || '—'}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: isCredit ? '#27ae60' : '#e74c3c' }}>
                                {isCredit ? '+' : '-'}₹{parseFloat(t.amount).toFixed(2)}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#444' }}>
                                ₹{parseFloat(t.balance_after).toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {walletPages > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
                        <button className="pagination-btn" disabled={walletPage <= 1 || walletLoading}
                          onClick={() => fetchWallet(walletCustomer.id, walletPage - 1)}>← Prev</button>
                        <span style={{ lineHeight: '32px', fontSize: '0.825rem' }}>Page {walletPage} of {walletPages}</span>
                        <button className="pagination-btn" disabled={walletPage >= walletPages || walletLoading}
                          onClick={() => fetchWallet(walletCustomer.id, walletPage + 1)}>Next →</button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ── Load from Receipt sub-modal ── */}
            {showLoadModal && (
              <div
                style={{ position: 'absolute', inset: 0, zIndex: 10, borderRadius: '12px', display: 'flex' }}
                onClick={() => setShowLoadModal(false)}
              >
                <div
                  style={{ background: '#fff', borderRadius: '12px', width: '100%', display: 'flex', flexDirection: 'column' }}
                  onClick={e => e.stopPropagation()}
                >
                  <div className="modal-header">
                    <h2 style={{ fontSize: '1rem' }}>Load Wallet from Receipt</h2>
                    <button className="modal-close" onClick={() => setShowLoadModal(false)}>×</button>
                  </div>

                  <div style={{ padding: '0 1.25rem 0.75rem', borderBottom: '1px solid #f0f0f0' }}>
                    <input
                      className="search-input"
                      style={{ width: '100%', boxSizing: 'border-box' }}
                      placeholder="Search by name…"
                      value={receiptSearch}
                      onChange={e => searchReceipts(e.target.value)}
                    />
                  </div>

                  <div style={{ overflowY: 'auto', flex: 1 }}>
                    {receiptsLoading ? (
                      <p style={{ padding: '1rem', color: '#888', fontSize: '0.875rem' }}>Loading…</p>
                    ) : receipts.length === 0 ? (
                      <p style={{ padding: '1rem', color: '#888', fontSize: '0.875rem', fontStyle: 'italic' }}>No receipts with available balance.</p>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.825rem' }}>
                        <thead>
                          <tr style={{ background: '#f8f9fa' }}>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef', width: '24px' }}></th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Name</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Date</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>Rcpt Amt</th>
                            <th style={{ padding: '6px 8px', textAlign: 'right', borderBottom: '1px solid #e9ecef' }}>Available</th>
                            <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #e9ecef' }}>Account</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receipts.map(r => (
                            <tr
                              key={r.id}
                              onClick={() => handleSelectReceipt(r)}
                              style={{ cursor: 'pointer', background: selectedReceipt?.id === r.id ? '#f0fdf4' : 'transparent', borderBottom: '1px solid #f5f5f5' }}
                            >
                              <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                                <input type="radio" readOnly checked={selectedReceipt?.id === r.id} />
                              </td>
                              <td style={{ padding: '6px 8px', fontWeight: selectedReceipt?.id === r.id ? 600 : 400 }}>{r.name}</td>
                              <td style={{ padding: '6px 8px', color: '#666', whiteSpace: 'nowrap' }}>
                                {r.receipt_date ? new Date(String(r.receipt_date).slice(0,10)+'T12:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                              </td>
                              <td style={{ padding: '6px 8px', textAlign: 'right' }}>₹{parseFloat(r.receipt_amount).toFixed(2)}</td>
                              <td style={{ padding: '6px 8px', textAlign: 'right', color: '#16a34a', fontWeight: 600 }}>₹{parseFloat(r.balance_amount).toFixed(2)}</td>
                              <td style={{ padding: '6px 8px', color: '#888', fontSize: '0.75rem' }}>{r.account_no || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {selectedReceipt && (
                    <div style={{ padding: '0.875rem 1.25rem', borderTop: '1px solid #e9ecef', background: '#fafafa' }}>
                      <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.5rem' }}>
                        Selected: <strong>{selectedReceipt.name}</strong> · Available: <strong style={{ color: '#16a34a' }}>₹{parseFloat(selectedReceipt.balance_amount).toFixed(2)}</strong>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <label style={{ fontSize: '0.825rem', whiteSpace: 'nowrap' }}>Amount to load:</label>
                        <input
                          className="form-input"
                          type="number"
                          min="0.01"
                          step="0.01"
                          max={parseFloat(selectedReceipt.balance_amount)}
                          value={loadAmount}
                          onChange={e => setLoadAmount(e.target.value)}
                          style={{ width: '120px' }}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#888' }}>max ₹{parseFloat(selectedReceipt.balance_amount).toFixed(2)}</span>
                      </div>
                      {loadError && <p style={{ color: '#e74c3c', fontSize: '0.8rem', marginTop: '4px' }}>{loadError}</p>}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '0.75rem' }}>
                        <button className="btn-cancel" onClick={() => setShowLoadModal(false)}>Cancel</button>
                        <button className="btn-submit" disabled={loadSaving} onClick={handleLoadFromReceipt}>
                          {loadSaving ? 'Loading…' : 'Load Wallet'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Interactions Modal ─────────────────────────────── */}
      {showIntModal && (
        <div className="modal-overlay" onClick={() => setShowIntModal(false)}>
          <div className="int-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💬 Interactions — {intCustomer?.customer_name}</h2>
              <button className="modal-close" onClick={() => setShowIntModal(false)}>×</button>
            </div>

            <div className="int-modal-body">
              {intError && (
                <div className="alert alert-error" style={{ margin: '0.75rem 1.5rem 0' }}>
                  <span className="alert-icon">⚠</span>{intError}
                  <button className="alert-close" onClick={() => setIntError('')}>×</button>
                </div>
              )}
              {/* Add interaction form */}
              <div className={`int-add-section${intFormOpen ? '' : ' int-add-collapsed'}`}>
                <button className="int-add-toggle" onClick={() => setIntFormOpen(p => !p)}>
                  <span className="cu-section-title" style={{ border: 'none', margin: 0, padding: 0 }}>Add Interaction</span>
                  <span className="int-chevron">{intFormOpen ? '▲' : '▼'}</span>
                </button>
                {intFormOpen && <>
                <div className="int-form-grid">
                  <div className="form-group">
                    <label>Type *</label>
                    <input
                      className="form-input"
                      list="int-type-opts"
                      placeholder="Call, Email, Visit…"
                      value={intForm.interaction_type}
                      onChange={e => setIntForm(p => ({ ...p, interaction_type: e.target.value }))}
                    />
                    <datalist id="int-type-opts">
                      {['Call','Email','WhatsApp','SMS','Visit','Meeting','Demo','Follow-up'].map(t => (
                        <option key={t} value={t} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group">
                    <label>Direction</label>
                    <select
                      className="form-select"
                      value={intForm.direction}
                      onChange={e => setIntForm(p => ({ ...p, direction: e.target.value }))}
                    >
                      <option value="">— select —</option>
                      <option value="Inbound">Inbound</option>
                      <option value="Outbound">Outbound</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Channel</label>
                    <input
                      className="form-input"
                      list="int-channel-opts"
                      placeholder="Phone, Email, WhatsApp…"
                      value={intForm.channel}
                      onChange={e => setIntForm(p => ({ ...p, channel: e.target.value }))}
                    />
                    <datalist id="int-channel-opts">
                      {['Phone','Email','WhatsApp','In-Person','Video Call','SMS'].map(c => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                  </div>
                  <div className="form-group int-full">
                    <label>Summary</label>
                    <textarea
                      className="form-input"
                      rows={2}
                      placeholder="What was discussed…"
                      value={intForm.summary}
                      onChange={e => setIntForm(p => ({ ...p, summary: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Outcome</label>
                    <input
                      className="form-input"
                      placeholder="e.g. Interested, Not reachable…"
                      value={intForm.outcome}
                      onChange={e => setIntForm(p => ({ ...p, outcome: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Action</label>
                    <input
                      className="form-input"
                      placeholder="e.g. Send proposal"
                      value={intForm.next_action}
                      onChange={e => setIntForm(p => ({ ...p, next_action: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Action Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={intForm.next_action_date}
                      onChange={e => setIntForm(p => ({ ...p, next_action_date: e.target.value }))}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button
                    className="btn-submit"
                    onClick={handleSaveInteraction}
                    disabled={savingInt || !intForm.interaction_type.trim()}
                  >
                    {savingInt ? 'Saving…' : 'Save Interaction'}
                  </button>
                </div>
                </>}
              </div>

              {/* Interactions list */}
              <div className="int-list-section">
                <div className="cu-section-title">
                  History {interactions.length > 0 && <span style={{ fontWeight: 400, fontSize: '0.75rem' }}>({interactions.length})</span>}
                </div>
                {loadingInts ? (
                  <p style={{ color: '#7f8c8d', fontSize: '0.875rem' }}>Loading…</p>
                ) : interactions.length === 0 ? (
                  <p style={{ color: '#7f8c8d', fontSize: '0.875rem', fontStyle: 'italic' }}>No interactions recorded yet.</p>
                ) : (
                  <div className="int-list">
                    {interactions.map(i => (
                      <div key={i.id} className="int-item">
                        <div className="int-item-header">
                          <span className="int-type-badge">{i.interaction_type}</span>
                          {i.direction && <span className="int-direction">{i.direction}</span>}
                          {i.channel && <span className="int-channel">· {i.channel}</span>}
                          <span className="int-date">{new Date(i.interaction_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                          {i.interacted_by_name && <span className="int-by">by {i.interacted_by_name}</span>}
                          <button
                            className="cu-addr-del"
                            title="Delete interaction"
                            disabled={deletingInt === i.id}
                            onClick={() => handleDeleteInteraction(i.id)}
                          >🗑</button>
                        </div>
                        {i.summary && <p className="int-summary">{i.summary}</p>}
                        {(i.outcome || i.next_action || i.next_action_date) && (
                          <div className="int-footer">
                            {i.outcome && <span className="int-outcome">Outcome: {i.outcome}</span>}
                            {i.next_action && <span className="int-next">Next: {i.next_action}</span>}
                            {i.next_action_date && <span className="int-next-date">by {new Date(String(i.next_action_date).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-IN', { dateStyle: 'medium' })}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;
